"use client";

import React, { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ProductDetailHeader } from "@/components/products/ProductDetailHeader";
import { ProductOverview } from "@/components/products/ProductOverview";
import { ProductDetailsTab } from "@/components/products/ProductDetailsTab";
import { ProductAttributesTab } from "@/components/products/ProductAttributesTab";
import { ProductImagesTab } from "@/components/products/ProductImagesTab";
import { ProductVariantsTab } from "@/components/products/ProductVariantsTab";
import { ProductCalendarTab } from "@/components/products/ProductCalendarTab";
import { StockEntryModal } from "@/components/products/StockEntryModal";
import { useParams, useLocation } from "wouter";
import {
  useProductWithRelation,
  useUpdateProduct,
} from "@/services/product-service";

// Mock data types and functions
interface MockStore {
  id: string;
  name: string;
}

// Mock functions
const getMockStore = (storeId?: string): MockStore => ({
  id: storeId || "1",
  name: "Main Store",
});

export default function ProductDetailPage() {
  const params = useParams();
  const [, setLocation] = useLocation();
  const productId = params.id as string | undefined;

  // Debug product ID
  console.log("Product ID from params:", productId, typeof productId);
  const [showStockForm, setShowStockForm] = useState(false);
  const [selectedVariantForStock, setSelectedVariantForStock] = useState<
    string | null
  >(null);

  // Fetch product data with relations
  const {
    data: productWithRelation,
    isLoading,
    error,
  } = useProductWithRelation(productId || null);

  // Extract data from API response
  const product = productWithRelation?.product || null;
  const productAttributes = productWithRelation?.productAttributes || [];
  const productVariants = productWithRelation?.productVariants || [];

  // Additional debug logging
  console.log("Extracted data:", {
    product,
    productAttributes,
    productVariants,
  });

  // Check if product data is in a different format (fallback)
  if (!product && productWithRelation) {
    console.log(
      "Product not found in expected format, checking alternative structures..."
    );
    console.log("Full productWithRelation structure:", productWithRelation);

    // Check if the data is directly in productWithRelation (type assertion for debugging)
    const relationData = productWithRelation as any;
    if (relationData.id || relationData.name) {
      console.log("Found product data directly in productWithRelation");
    }
  }

  // Mock data for store (replace with real store data when available)
  const store = getMockStore();

  if (isLoading) {
    return (
      <div className="space-y-6 p-6">
        <div className="flex items-center space-x-4">
          <button
            className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2"
            onClick={() => setLocation("/products")}
          >
            ← Back to Products
          </button>
        </div>
        <div className="flex flex-col items-center justify-center py-12">
          <h3 className="text-lg font-semibold">Loading product...</h3>
        </div>
      </div>
    );
  }

  if (error || !product || !productId) {
    return (
      <div className="space-y-6 p-6">
        <div className="flex items-center space-x-4">
          <button
            className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2"
            onClick={() => setLocation("/products")}
          >
            ← Back to Products
          </button>
        </div>
        <div className="flex flex-col items-center justify-center py-12">
          <h3 className="text-lg font-semibold">Product not found</h3>
          <p className="text-muted-foreground">
            {error?.message || "The product you're looking for doesn't exist."}
          </p>
          {error && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
              <h4 className="font-medium text-red-800">Error Details:</h4>
              <pre className="text-sm text-red-700 mt-2 whitespace-pre-wrap">
                {JSON.stringify(error, null, 2)}
              </pre>
            </div>
          )}
          {productWithRelation && (
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
              <h4 className="font-medium text-blue-800">API Response:</h4>
              <pre className="text-sm text-blue-700 mt-2 whitespace-pre-wrap">
                {JSON.stringify(productWithRelation, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </div>
    );
  }

  const handleAddStock = () => {
    // Stock addition is now handled within the modal
    setShowStockForm(false);
    setSelectedVariantForStock(null);
    // Optionally refresh product data here to update stock quantities
  };

  const handleQuickAddStock = (variantId: string) => {
    setSelectedVariantForStock(variantId);
    setShowStockForm(true);
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <ProductDetailHeader
        product={product}
        onEdit={() => setLocation(`/products/${productId}/edit`)}
      />

      {/* Product Overview */}
      <ProductOverview
        product={product}
        productVariants={productVariants}
        store={store}
      />

      <Tabs defaultValue="details" className="space-y-6">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="variants">Variants</TabsTrigger>
          <TabsTrigger value="attributes">Attributes</TabsTrigger>
          <TabsTrigger value="images">Images</TabsTrigger>
          <TabsTrigger value="calendar">Calendar</TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="space-y-6">
          <ProductDetailsTab product={product} store={store} />
        </TabsContent>

        <TabsContent value="attributes" className="space-y-6">
          <ProductAttributesTab productAttributes={productAttributes} />
        </TabsContent>

        <TabsContent value="images" className="space-y-6">
          <ProductImagesTab
            product={product}
            onEdit={() => setLocation(`/products/${productId}/edit`)}
          />
        </TabsContent>

        <TabsContent value="variants" className="space-y-6">
          <ProductVariantsTab
            productId={productId}
            productCode={product?.code || ""}
            productVariants={productVariants}
            productAttributes={productAttributes}
            onAddStock={handleQuickAddStock}
          />
        </TabsContent>

        <TabsContent value="calendar" className="space-y-6">
          <ProductCalendarTab onScheduleStock={() => setShowStockForm(true)} />
        </TabsContent>
      </Tabs>

      {/* Stock Entry Modal */}
      <StockEntryModal
        productId={productId}
        isOpen={showStockForm}
        selectedVariantId={selectedVariantForStock}
        productVariants={productVariants}
        onSubmit={handleAddStock}
        onCancel={() => {
          setShowStockForm(false);
          setSelectedVariantForStock(null);
        }}
      />
    </div>
  );
}
